// ─────────────────────────────────────────────────────────────────────────────
// daemonService.js — HTTP + WebSocket client for the NPQ VPN desktop daemon
//
// READ-ONLY BY DESIGN. The desktop app owns the VPN; the dashboard only
// observes it. This client can ask the daemon three questions — are you
// running, who is signed in, what is the tunnel doing — and subscribe to state
// changes. It cannot start or stop a tunnel, and must not gain that ability.
//
// The tunnel-control calls (/routing/connect-wireguard, /routing/disconnect-
// wireguard, /routing/connect-enterprise, /routing/disconnect-enterprise,
// /vpn/wireguard-public-key) were removed on 2026-07-31: nothing in the UI ever
// called them, and leaving a browser-reachable path to "stop this user's VPN"
// wired up is a liability. The daemon still exposes those endpoints; the
// intended follow-up is for it to refuse them from browser origins outright.
//
// Daemon URLs read from env vars (set in .env.development / .env.production):
//   REACT_APP_DAEMON_HTTP_URL  default: http://127.0.0.1:7979
//   REACT_APP_DAEMON_WS_URL   default: ws://127.0.0.1:7979
// Full API spec: desktop-app/docs/web/daemon-api.md
// ─────────────────────────────────────────────────────────────────────────────

const DAEMON_HTTP = process.env.REACT_APP_DAEMON_HTTP_URL || "http://127.0.0.1:7979";
// Derive the WebSocket base from the HTTP URL so both always point at the same
// host/port. REACT_APP_DAEMON_WS_URL can still override if needed, but it must
// include the /events path (or the derived path below is used instead).
const DAEMON_WS   = process.env.REACT_APP_DAEMON_WS_URL
  ? process.env.REACT_APP_DAEMON_WS_URL.replace(/\/?$/, "/events")
  : DAEMON_HTTP.replace(/^http/, "ws") + "/events";
const PROBE_TIMEOUT_MS = 800;

// ── Shared fetch helper ───────────────────────────────────────────────────────

// GET only, deliberately. Every remaining call is a read, and the helper no
// longer accepts a method or body — so re-introducing a write means editing
// this function, which is a visible decision rather than a one-line slip.
async function daemonFetch(path) {
  const res = await fetch(`${DAEMON_HTTP}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const data = await res.json();

  if (!res.ok) {
    // Attach machine-readable code to the thrown error
    const err = new Error(data?.message || `Daemon error ${res.status}`);
    err.code  = data?.code || "UNKNOWN";
    throw err;
  }

  return data;
}

// ── Health probe ──────────────────────────────────────────────────────────────

/**
 * Returns true if the daemon is reachable, false otherwise.
 * Uses a tight AbortSignal timeout so the UI doesn't hang.
 */
export async function isDaemonRunning() {
  try {
    const res = await fetch(`${DAEMON_HTTP}/health`, {
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    const data = await res.json();
    return !!data?.ok;
  } catch {
    return false;
  }
}

/**
 * Returns the full health payload: { ok, version, daemonVersion }
 * Throws if the daemon is unreachable.
 */
export async function getDaemonHealth() {
  return daemonFetch("/health");
}

// ── Identity check ────────────────────────────────────────────────────────────

/**
 * Returns { loggedIn: false }
 *      or { loggedIn: true, userId, email }
 *
 * DaemonContext compares `userId` against the dashboard session to detect the
 * app being signed in as somebody else.
 */
export async function getDaemonUser() {
  return daemonFetch("/daemon/whoami");
}

// ── VPN status snapshot ───────────────────────────────────────────────────────

/**
 * Returns the current VpnStatus object.
 * Prefer subscribing to WebSocket events for live updates — use this only
 * for the initial synchronous snapshot before the WS connects.
 *
 * VpnStatus shape (state field is the discriminant):
 *   { state: "disconnected" }
 *   { state: "connecting",    startedAt: number }
 *   { state: "connected",     connectedAt: number, routeId: string, shieldLevel?: string }
 *   { state: "disconnecting" }
 *   { state: "error",         message: string }
 */
export async function getVpnStatus() {
  const { status } = await daemonFetch("/vpn/status");
  return status;
}

// ── WebSocket event subscription ──────────────────────────────────────────────

/**
 * Connects to ws://127.0.0.1:7979/events and calls handler functions on
 * incoming events. Auto-reconnects on close unless stop() has been called.
 *
 * Handlers:
 *   onStatusChanged(vpnStatus)  — fired on every VPN state transition
 *   onBackendDisconnected()     — fired when backend circuit teardown completes
 *   onOpen()                    — optional, fired when WS connects
 *   onClose()                   — optional, fired on every close (incl. before reconnect)
 *
 * Returns a stop() function that permanently closes the WS and stops reconnecting.
 *
 * Usage:
 *   const stop = connectDaemonEvents({
 *     onStatusChanged:       (s) => dispatch(setVpnStatus(s)),
 *     onBackendDisconnected: ()  => dispatch(fetchDashboard()),
 *   });
 *   // later:
 *   stop();
 */
export function connectDaemonEvents(handlers) {
  let ws      = null;
  let stopped = false;

  function connect() {
    if (stopped) return;

    ws = new WebSocket(DAEMON_WS);

    ws.onopen = () => {
      handlers.onOpen?.();
    };

    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }

      if (msg.type === "vpn:statusChanged") {
        handlers.onStatusChanged?.(msg.data);
      } else if (msg.type === "vpn:backendDisconnected") {
        handlers.onBackendDisconnected?.();
      }
    };

    ws.onerror = () => {
      // onerror always precedes onclose — do nothing here, let onclose drive
      // reconnect logic. Calling ws.close() here is redundant and can race
      // with the stop() function ("closed before connection established").
    };

    ws.onclose = () => {
      handlers.onClose?.();
      if (!stopped) {
        // Back-off 3 s before reconnecting
        setTimeout(connect, 3000);
      }
    };
  }

  connect();

  return function stop() {
    stopped = true;
    // Guard against closing a socket that is still in CONNECTING state (readyState 0).
    // Calling close() on a CONNECTING socket produces a browser warning:
    // "WebSocket is closed before the connection is established".
    // The onclose handler will fire regardless and won't schedule a reconnect
    // because stopped is already true.
    if (ws && ws.readyState !== WebSocket.CONNECTING) ws.close();
  };
}
